import React, { useEffect, useRef } from 'react';
import type { RemoteAudioTrack } from 'livekit-client';

// Shared AudioContext for all LiveKit remote audio. Rendering these tracks through
// Web Audio (rather than plain WebRTC <audio> playout) keeps them out of Chrome's
// WebRTC render mix, which the echo-cancel loopback (use-echo-cancellation.tsx)
// would otherwise pull into its output — bleeding remote audio through media. One
// shared context across all feeds stays under Chrome's AudioContext limit.
let sharedAudioContext: AudioContext | null = null;
const getSharedAudioContext = (): AudioContext => {
	if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
		sharedAudioContext = new AudioContext();
	}
	// resume if the browser auto-suspended it (autoplay policy / page blur); no-op if running
	if (sharedAudioContext.state === 'suspended') {
		void sharedAudioContext.resume();
	}
	return sharedAudioContext;
};

interface Options {
	muted: boolean;
	volume: number;
}

/**
 * Renders a LiveKit RemoteAudioTrack via Web Audio into the given <audio> element,
 * keeping it out of Chrome's WebRTC render mix (prevents echo-cancel bleed). Volume
 * runs through a plugin gain node initialised to 0, so a feed that should be silent
 * (muted, or staged in the next spotlight shot) is never briefly audible before its
 * level is applied.
 */
export default function useLivekitAudioPlayback(
	audioTrack: RemoteAudioTrack | null,
	audioRef: React.RefObject<HTMLAudioElement | null>,
	{ muted, volume }: Options,
) {
	const gainNodeRef = useRef<GainNode | null>(null);

	useEffect(() => {
		const element = audioRef.current;
		if (!element || !audioTrack) return;

		const ctx = getSharedAudioContext();
		const gainNode = ctx.createGain();
		gainNode.gain.value = 0;
		gainNodeRef.current = gainNode;

		// plugins / context must be set before attach so connectWebAudio picks them up
		audioTrack.setWebAudioPlugins([gainNode]);
		audioTrack.setAudioContext(ctx);
		audioTrack.attach(element);

		// eslint-disable-next-line consistent-return
		return () => {
			audioTrack.detach(element);
			// setAudioContext(undefined) is LiveKit's teardown for the Web Audio
			// connection — it disconnects the internal source/gain nodes
			audioTrack.setAudioContext(undefined);
			audioTrack.setWebAudioPlugins([]);
			gainNode.disconnect();
			gainNodeRef.current = null;
		};
	}, [audioTrack, audioRef]);

	useEffect(() => {
		const gainNode = gainNodeRef.current;
		if (!gainNode) return;
		// re-resume in case the context auto-suspended since attach, else the ramp
		// would be scheduled on a frozen context and never apply
		const ctx = gainNode.context as AudioContext;
		if (ctx.state === 'suspended') void ctx.resume();
		// short ramp is click-free and effectively immediate
		gainNode.gain.setTargetAtTime(muted ? 0 : volume, ctx.currentTime, 0.02);
		// audioTrack in deps: re-applies volume after a new track re-attaches and resets gainNodeRef
	}, [muted, volume, audioTrack]);
}
